import { Autocomplete, CircularProgress } from '@mui/material';
import { makeStyles } from '@mui/styles';
import * as React from 'react';

import { ContextStore } from '../store/contextStore';
import { DataAPIContext, GET_LIST } from '../api';
import { debounce } from '../utils';
import FormLicenseeContext from './FormLicenseeContext';
import images from "./images"
import StyledFilledTextField from './StyledFilledTextField';

const useStyles = makeStyles((theme) => ({
    //Options styling
    menuList: {
        //Limit option list height for preventing overFlow with the top Navbar
        maxHeight: '250px !important',
        '& li.Mui-focused': {},
        '& li': {},
    },
}));
const RemoteAutocomplete = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { settings, isFranchise } = React.useContext(ContextStore);
    const formLicensee = React.useContext(FormLicenseeContext);

    const [open, setOpen] = React.useState(false);
    const [staleOptions, setStaleOptions] = React.useState(true);
    const [options, setOptions] = React.useState([]);
    const [loadedEmptyOptions, setLoadedEmptyOptions] = React.useState(false);
    const loading = open && (staleOptions || (options.length <= 0 && !loadedEmptyOptions));
    const [query, setQuery] = React.useState(null);
    const inputRef = React.useRef(null);
    const urlSuffix = props.urlSuffix === void 0 ? 'autocomplete/' : props.urlSuffix;
    const classes = useStyles();

    React.useEffect(() => {
        let active = true;
        const tm = setTimeout(async () => {
            const params = {
                convert: props.convert,
                filter: { query: props.query || query, filters: undefined },
                urlSuffix: urlSuffix,
            };
            if (formLicensee) {
                params.filter = { ...params.filter, franchise_to: formLicensee };
            }
            if (props.apiParams) {
                params.filter = { ...params.filter, ...props.apiParams };
            }
            if (props.apiFilters) {
                params.filter.filters = JSON.stringify(props.apiFilters);
            }

            if (props.optionsResultsSize) {
                params.pagination = { page: 1, perPage: props.optionsResultsSize };
            }
            const res = await dataAPI(GET_LIST, props.resource, params);

            if (active) {
                if (props.mapResults) {
                    setOptions(props.mapResults(res.data, res));
                } else {
                    setOptions(res.data);
                }
                setStaleOptions(false);
                if (res.data.length <= 0) {
                    setLoadedEmptyOptions(true);
                }
            }
        }, 500);

        if (!loading) {
            return undefined;
        }
        setLoadedEmptyOptions(false);

        return () => {
            active = false;
            clearTimeout(tm);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, query]);

    React.useEffect(() => {
        if (!open) {
            setOptions([]);
        }
    }, [open]);

    const acProps = {};
    if (props.multiple) {
        acProps.multiple = true;
    }
    if (props.disabled) {
        acProps.disabled = true;
    }
    if (props.hasOwnProperty('value')) {
        acProps.value = props.value;
    }
    if (props.hasOwnProperty('defaultValue')) {
        acProps.defaultValue = props.defaultValue;
    }
    if (props.style) {
        acProps.style = props.style;
    }
    if (props.width) {
        acProps.style = Object.assign({}, props.style, { width: `${props.width}px` });
    }
    if (props.fullWidth) {
        acProps.fullWidth = true;
    }
    const onQueryChange = debounce((ev) => {
        if (inputRef.current) {
            setStaleOptions(true);
            console.log('query change', inputRef.current.value);
            setQuery(inputRef.current.value);
            props.onQueryChange && props.onQueryChange(inputRef.current.value);
        }
    }, 300);

    const textFieldProps = { required: props.required, ...props.textFieldProps };

    const onChange = React.useCallback((ev, nv, reason) => {
        if (reason === 'clear') {
            setQuery(null);
        }
        props.onChange && props.onChange(ev, nv, reason);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const values = props.value && props.value.constructor === Array ? props.value : props.value ? [props.value] : [];
    const valuesIds = values.map((v) => v?.id);

    const filteredOptions = props.filterSelected ? options.filter((opt) => valuesIds.indexOf(opt.id) < 0) : options;

    const baseGetOptionLabel = props.getOptionLabel || ((option) => (option && option.name ? option.name : ''));
    const getOptionLabel = isFranchise
        ? (option) => {
            const label = baseGetOptionLabel(option);
            const licensee = option?.tour_operator?.name;
            return licensee ? `${label} [${licensee}]` : label;
        }
        : baseGetOptionLabel;

    const getOptionSelected = (option, value) => {
        // console.log('getOptionSelected', acProps.multiple, option, value);
        if (!value || !value.constructor) {
            return true;
        }
        if (acProps.multiple) {
            const values = value.constructor === Array ? value : [value];
            // console.log('getOptionSelected', option, values);
            return values.indexOf(option.id) >= 0;
        } else {
            if (typeof value === 'number' || typeof value === 'string') {
                return value === option.id;
            } else {
                return value.id === option.id;
            }
        }
    };

    return (
        <Autocomplete
            {...acProps}
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            getOptionSelected={props.getOptionSelected || getOptionSelected}
            getOptionLabel={getOptionLabel}
            renderOption={props.renderOption}
            noOptionsText={props.noOptionsText || 'No matches'}
            options={filteredOptions}
            filterOptions={props.filterOptions}
            loading={loading}
            onChange={onChange}
            classes={{ paper: classes.paper, option: classes.option, listbox: classes.listbox }}
            onBlur={props.onBlur}
            clearOnBlur={props.clearOnBlur !== false}
            ListboxProps={{ className: classes.menuList }}
            isOptionEqualToValue={props.isOptionEqualToValue}
            renderInput={(params) => (
                <StyledFilledTextField
                    {...params}
                    {...textFieldProps}
                    label={props.label}
                    variant={props.variant}
                    size={props.size}
                    onChange={onQueryChange}
                    inputRef={inputRef}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" style={{ marginTop: '-18px' }} size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                        startAdornment: (
                            <React.Fragment>
                                {props.customInputIcon && props.value?.icon && (
                                    <img loading="lazy" width="20" style={{ marginTop: '-4px', paddingRight: '0.25rem', paddingLeft: '0.5rem' }} src={images.url(settings.images.base, props.value.icon)} alt={props.value?.name} />
                                )}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
        />
    );
};

export default RemoteAutocomplete;
